import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  ChevronLeft,
  ChevronRight,
  Gamepad2,
  Info,
  Meh,
  Search,
  SearchX,
  Users,
  X,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { Checkbox } from "../../components/Checkbox/Checkbox";
import { CountryCell } from "../../components/CountryCell/CountryCell";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Pill } from "../../components/Pill/Pill";
import { Skeleton } from "../../components/Skeleton/Skeleton";

import { ApiError, describeError } from "../../lib/errors";
import { formatRelativeShort } from "../../lib/format";
import { SEARCH_MIN_LENGTH, isSearchTooShort } from "../../lib/search";
// The verbose formatter the guild lists already use — "18 minutes ago",
// "3 months ago". Imported rather than copied: this file used to carry its own
// identical-looking version, which had drifted (days 360-364 came out as "0
// years ago") and had no test. The one below is tested in
// features/guilds/relativeTime.test.ts, and features/bots already reaches for
// it across features in the same way.
import { formatRelativeLong } from "../guilds/relativeTime";
import { useProject } from "../projects/useProject";
import { listPlayers } from "./api";
import { PLAYERS_PAGE_SIZE, playersPageQueryKey } from "./queryKeys";
import type { PlayerListItem } from "./types";
import styles from "./PlayersListPage.module.css";

// === Formatting helpers ============================================== //

/**
 * Build the page-button sequence with ellipsis gaps — mockup state 1 shows
 * `1 2 3 … 500`. Always shows first/last; a window around the current page.
 */
function pageRange(current: number, total: number): (number | "gap")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out: (number | "gap")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("gap");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push("gap");
  out.push(total);
  return out;
}

/**
 * The whole query string, rebuilt from what the page is actually showing.
 *
 * Every writer goes through here — the search-sync effect, the pager and both
 * checkboxes — because each of them replaces the string outright, and a filter
 * that one of them forgot to carry across would silently switch itself off.
 * That is exactly how `bots` used to vanish mid-keystroke, and there are three
 * filters now.
 *
 * Anything that is off is written as an ABSENT parameter rather than `=0`: the
 * default is the default on both sides, so the link a plain page produces is
 * the plain one.
 */
function buildSearch(v: {
  q: string;
  page: number;
  online: boolean;
  bots: boolean;
}): URLSearchParams {
  const next = new URLSearchParams();
  if (v.q) next.set("q", v.q);
  if (v.page > 1) next.set("page", String(v.page));
  if (v.online) next.set("online", "1");
  if (v.bots) next.set("bots", "1");
  return next;
}

// === Component ====================================================== //

export function PlayersListPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth for q + page + bots + online.
  const urlQ = searchParams.get("q") ?? "";
  const urlPage = Math.max(1, Number(searchParams.get("page")) || 1);
  // `Show bots` has no local state of its own: the URL is the whole of it, so
  // the switch survives a reload and travels in a shared link. Only "1" is on
  // — the same single value the endpoint reads, and the same one written below
  // — so a hand-edited `?bots=yes` shows the default rather than a guess.
  const bots = searchParams.get("bots") === "1";
  // `Online only`, by exactly the same rule — and it is what the `Players
  // online` band on Project Overview links into: with `bots` off by default,
  // this list holds exactly the population that band's headline counts.
  const online = searchParams.get("online") === "1";

  // Local search text — debounced before it drives the query / URL.
  const [q, setQ] = useState(urlQ);
  const [debouncedQ, setDebouncedQ] = useState(urlQ);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // A term below the minimum length never becomes a search: it is an empty box
  // for everything downstream — the request, the URL, the table and the count —
  // and the hint under the toolbar is what says so. Reading it off the
  // *debounced* term is what keeps the hint from flashing between the "F" and
  // the "r" of someone typing "Frodo" straight through.
  const tooShort = isSearchTooShort(debouncedQ);
  const searchQ = tooShort ? "" : debouncedQ;

  // Reflect the debounced query + page in the URL. A query change resets to
  // page 1 so paginated results never point at a stale offset. A short term is
  // not written: `?q=Fr` in a shared link fills the box and shows the hint, and
  // the URL comes back to the unfiltered page 1 it actually displays.
  //
  // Typing a short term counts as a query change even though `searchQ` stays
  // empty — someone on page 3 who starts typing gets page 1 of the whole list,
  // not page 3 of it. That is why `tooShort` is a dependency here: it is the
  // only signal that the box changed while `searchQ` did not. Paging on from
  // there still works, because neither dependency moves when the page does.
  //
  // This effect rebuilds the query string from nothing, so the two filters have
  // to be carried across explicitly — anything left out here is dropped, and a
  // search typed with the simulation shown would otherwise hide it
  // mid-keystroke. `buildSearch` is what keeps all three writers in step.
  useEffect(() => {
    const pageForUrl = tooShort || searchQ !== urlQ ? 1 : urlPage;
    setSearchParams(
      buildSearch({ q: searchQ, page: pageForUrl, online, bots }),
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, tooShort]);

  const page = searchQ !== urlQ ? 1 : urlPage;

  const query = useQuery({
    queryKey: playersPageQueryKey(id, searchQ, page, bots, online),
    queryFn: () =>
      listPlayers(id, {
        q: searchQ,
        page,
        limit: PLAYERS_PAGE_SIZE,
        bots,
        online,
      }),
    enabled: !!id,
  });

  // Project name for the breadcrumb.
  const { data: project } = useProject(id);

  const data = query.data;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // How many of `total` are the simulation. Zero from an answer that predates
  // the field, which is also the truth whenever `Show bots` is off.
  const botsInTotal = data?.bots ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PLAYERS_PAGE_SIZE));
  const hasSearch = searchQ.length > 0;

  function goToPage(p: number) {
    // Intentionally a history *push* (no `replace`) so Back returns to the
    // previous page — unlike the search-sync effect, which `replace`s so
    // individual keystrokes never pollute the browser history.
    setSearchParams(buildSearch({ q: searchQ, page: p, online, bots }));
  }

  /**
   * Tick or untick `Show bots`.
   *
   * `page` is deliberately not carried across. The two sets are different
   * lengths, so page 7 of one is not page 7 of the other — and on a project
   * whose bots are most of its rows, hiding them would leave the pager pointing
   * at an offset past the end and the operator looking at an empty table with
   * no clue why. Page 1 of what they just asked for is the answer.
   *
   * The search IS carried across, because it is the same filter seen from the
   * other side: someone who searched for a name and found nothing ticks this to
   * ask whether a bot is holding it.
   *
   * A push like goToPage, not a replace: this is a deliberate act, and Back
   * undoing it is what a browser is for.
   */
  function toggleBots(next: boolean) {
    setSearchParams(
      buildSearch({ q: searchQ, page: 1, online, bots: next }),
    );
  }

  /**
   * Tick or untick `Online only`.
   *
   * The same mechanics as `Show bots` above, for the same reasons: page 1 of
   * the set just asked for (the two sets are different lengths), the search
   * carried across (three independent narrowings of one list), and a push so
   * Back undoes it.
   *
   * `bots` is carried too, and it matters most in this direction: on a project
   * running the simulation, the online set is largely bots, and dropping the
   * flag while turning the filter on would answer a question nobody asked.
   */
  function toggleOnline(next: boolean) {
    setSearchParams(
      buildSearch({ q: searchQ, page: 1, online: next, bots }),
    );
  }

  function clearSearch() {
    setQ("");
  }

  const header = (
    <PageHeader
      breadcrumb={[
        { label: "All projects", to: "/projects" },
        { label: project?.name ?? "Project" },
        { label: "Players" },
      ]}
      title="Players"
    />
  );

  // --- Search field disabled when there is nothing to search:
  //   • brand-new project — total 0 with no active query (mockup state 3); or
  //   • the initial page load, before any data has arrived (mockup notes:
  //     "Loading: toolbar disabled"). It stays enabled during search-driven
  //     refetches so typing is never interrupted.
  //
  // Text in the box keeps it enabled whatever the count says. Otherwise a
  // `?q=Fr` link into a project with no players would disable the field with
  // that text still in it — nothing to search, and no way to clear it either.
  const initialLoading = query.isLoading && !hasSearch && !data;
  const searchDisabled =
    initialLoading ||
    (!query.isLoading &&
      !query.isError &&
      !hasSearch &&
      q.length === 0 &&
      total === 0);

  const searchField = (
    <div
      className={`${styles.searchInput} ${
        searchDisabled ? styles.searchDisabled : ""
      }`}
    >
      <span className={styles.leading}>
        <Search size={14} aria-hidden="true" />
      </span>
      <input
        placeholder="Search by name, external ID, or UUID…"
        aria-label="Search players"
        value={q}
        disabled={searchDisabled}
        onChange={(e) => setQ(e.target.value)}
      />
      {q.length > 0 && (
        <button
          type="button"
          className={styles.clear}
          aria-label="Clear search"
          onClick={clearSearch}
        >
          <X size={13} aria-hidden="true" />
        </button>
      )}
    </div>
  );

  // === Error state ==================================================== //
  if (query.isError) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.errorCard}>
            <Alert variant="danger" title="Couldn't load players">
              {query.error instanceof ApiError
                ? describeError(query.error.code)
                : describeError("internal")}
            </Alert>
            <div className={styles.errorActions}>
              <Button
                variant="secondary"
                onClick={() => query.refetch()}
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Render ========================================================= //
  return (
    <>
      {header}
      <div className={styles.page}>
        {/* Toolbar — search + Show bots + count chip / result line
            (players-list-show-bots.html states 1-2). The spacer and the
            checkbox are outside the `!hasSearch` branch the chip keeps: the
            box has to be reachable DURING a search too, since the two are one
            filter and someone who searched a name and found nothing is exactly
            who needs to ask whether a bot is holding it. */}
        <div className={styles.toolbar}>
          {searchField}
          <div className={styles.toolbarSpacer} />
          <Checkbox
            checked={online}
            onChange={toggleOnline}
            label="Online only"
          />
          <Checkbox checked={bots} onChange={toggleBots} label="Show bots" />
          {/* The same chip in two liveries. With the filter on it counts a
              different thing — who is connected, not who exists — so it says a
              different word and wears the presence colour; the people glyph
              gives way to the same live dot the Overview band uses. */}
          {!hasSearch && !query.isLoading && total > 0 && (
            <span
              className={`${styles.countCallout} ${online ? styles.ok : ""}`}
            >
              {online ? (
                <span className={styles.chipDot} aria-hidden="true" />
              ) : (
                <Users size={11} aria-hidden="true" />
              )}
              {total.toLocaleString()} {online ? "online" : "players"}
            </span>
          )}
        </div>

        {/* Minimum-length hint — the result line's slot, so the table does not
            move when the third character turns it into a real search. It is a
            live region because it is the only thing that changes: the result
            line arrives with a new table, this one arrives to explain why the
            table stayed as it was. */}
        {tooShort && (
          <div className={styles.hintLine} role="status">
            <Info size={13} aria-hidden="true" />
            <span>
              <b>Type at least {SEARCH_MIN_LENGTH} characters</b> to search ·
              showing all players meanwhile
            </span>
          </div>
        )}

        {/* Search result line (mockup state 2). */}
        {hasSearch && !query.isLoading && total > 0 && (
          <div className={styles.resultLine}>
            <b>{total.toLocaleString()}</b>{" "}
            {total === 1 ? "player matches" : "players match"}{" "}
            <code>{searchQ}</code> · substring of the display name ·{" "}
            <button
              type="button"
              className={styles.link}
              onClick={clearSearch}
            >
              Clear search
            </button>
          </div>
        )}

        {/* Presence result line (mockup state 2).

            Only when `Show bots` is on as well, and that is the whole of the
            condition: the sentence exists to break the online figure into
            living players and simulation, and with the bots excluded from the
            set the second number is 0 by construction — a line that says
            "of them 0 are bots" about a set bots were filtered out of tells
            the reader nothing and implies something false.

            It sits in its own row rather than in the search line's slot: the
            two say different things (which term is filtering, versus what the
            online figure is made of), and when both apply neither is a
            substitute for the other. */}
        {online && bots && !query.isLoading && total > 0 && (
          <div className={styles.resultLine}>
            Of them <b>{botsInTotal.toLocaleString()}</b>{" "}
            {botsInTotal === 1 ? "is a bot" : "are bots"}. Taken{" "}
            <b>just now</b>; presence lasts 60 seconds without a heartbeat.
          </div>
        )}

        {/* Table / states. */}
        <div className={styles.tableWrap}>
          {query.isLoading ? (
            <LoadingTable />
          ) : items.length === 0 ? (
            hasSearch ? (
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <SearchX size={26} aria-hidden="true" />
                </div>
                <h3>No player matches &quot;{searchQ}&quot;</h3>
                <p>
                  Try part of a name, the external ID, or the player UUID.
                </p>
                <button
                  type="button"
                  className={styles.clearLink}
                  onClick={clearSearch}
                >
                  Clear search
                </button>
              </div>
            ) : online ? (
              // Nobody is connected (mockup state 3). It comes after the search
              // branch, not before it: with a term in the box the reader is
              // asking about that name, and the box with its own ✕ is right
              // there — while this state's whole value is naming the two
              // switches that produced it.
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <Meh size={26} aria-hidden="true" />
                </div>
                <h3>Nobody is online right now</h3>
                {/* Which sentence depends on `Show bots`, because the advice
                    is a list of switches to reach for and one of them may
                    already be down. With the simulation shown, "no living
                    player is online" is not what was asked and not the whole
                    answer — the bots were in the set and they are not there
                    either — and pointing at a ticked box is advice that leads
                    nowhere. The mockup draws this state for `online=1 ·
                    bots=0`, which is the first branch. */}
                {bots ? (
                  <p>
                    Nobody, living or simulated, is online. Clear{" "}
                    <b>Online only</b> to see the whole list.
                  </p>
                ) : (
                  <p>
                    No living player is online. Clear <b>Online only</b> to see
                    the whole list, or turn on <b>Show bots</b> — the simulation
                    can be online when nobody else is.
                  </p>
                )}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <Gamepad2 size={26} aria-hidden="true" />
                </div>
                <h3>No players yet</h3>
                <p>
                  Players appear here automatically the first time someone
                  signs in to the game from your build. Nothing to set up.
                </p>
              </div>
            )
          ) : (
            <>
              <PlayersTable
                items={items}
                onRowClick={(playerId) =>
                  navigate(`/projects/${id}/players/${playerId}`)
                }
              />
              {/* Pagination footer — hidden on a single page of results. */}
              {totalPages > 1 && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  itemsOnPage={items.length}
                  onGoToPage={goToPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// === Players table ================================================== //

interface PlayersTableProps {
  items: PlayerListItem[];
  onRowClick: (playerId: string) => void;
}

function PlayersTable({ items, onRowClick }: PlayersTableProps) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {/* 34%, not the 38% this column had before Last seen joined it —
              the mockup's own widths for the six-column table. */}
          <th style={{ width: "34%" }}>Player</th>
          <th style={{ width: 96 }}>Country</th>
          {/* Between Country and Joined, and beside Updated rather than in
              place of it: Updated is when the profile was last edited, which
              an admin can do to a player who has never connected. */}
          <th style={{ width: 150 }}>Last seen</th>
          <th>Joined</th>
          <th>Updated</th>
          <th style={{ width: 36 }} aria-label="Open player" />
        </tr>
      </thead>
      <tbody>
        {items.map((p) => (
          <tr
            key={p.id}
            role="link"
            tabIndex={0}
            aria-label={`Open ${p.display_name}`}
            onClick={() => onRowClick(p.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick(p.id);
              }
            }}
          >
            <td>
              <div className={styles.playerCell}>
                {/* The dot rides the avatar so a row reads as online while the
                    eye is still going down the names, before it reaches Last
                    seen. The wrapper exists only to give it something to be
                    positioned against. */}
                <span className={styles.avWrap}>
                  <Avatar name={p.display_name} size={32} />
                  {p.online && (
                    <span className={styles.presenceDot} aria-hidden="true" />
                  )}
                </span>
                <span className={styles.info}>
                  {/* The badge rides on the name's line, so the external id
                      below keeps its own. Same markup as the guild chat feed's
                      member events — one Pill, one lucide Bot, nothing new. */}
                  <span className={styles.nameRow}>
                    <span className={styles.name}>{p.display_name}</span>
                    {p.is_bot && (
                      <Pill size="sm">
                        <span className={styles.botMark}>
                          <Bot size={10} aria-hidden="true" />
                          Bot
                        </span>
                      </Pill>
                    )}
                  </span>
                  <span className={styles.eid}>{p.external_id}</span>
                </span>
              </div>
            </td>
            <td>
              <CountryCell code={p.country} />
            </td>
            <td>
              <LastSeen player={p} />
            </td>
            <td className={styles.muted}>
              {formatRelativeLong(p.created_at)}
            </td>
            <td className={styles.muted}>
              {formatRelativeShort(p.updated_at)}
            </td>
            <td className={styles.chevCell}>
              <ChevronRight size={14} aria-hidden="true" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// === Last seen cell ================================================= //

/**
 * One column, three states — never two columns and never a blank.
 *
 * `online` decides the first of them and not arithmetic on `last_seen_at`:
 * presence is read from Redis against a 60-second threshold, and a browser
 * comparing a timestamp against its own clock would disagree with the server
 * about which of them is stale.
 *
 * `last_seen_at === null` is its own state and reads `Never` — an account that
 * has never opened a socket is not the same as one that opened one long ago,
 * and printing "56 years ago" over an epoch-zero fallback is how that
 * distinction usually gets lost.
 *
 * The third is `lastSeenText` below, and it is deliberately not
 * `formatRelativeLong` bare.
 */
function LastSeen({ player }: { player: PlayerListItem }) {
  if (player.online) {
    return (
      <span className={`${styles.lastSeen} ${styles.online}`}>
        <span className={styles.d} aria-hidden="true" />
        Online
      </span>
    );
  }
  if (!player.last_seen_at) {
    return (
      <span className={`${styles.lastSeen} ${styles.never}`}>Never</span>
    );
  }
  return (
    <span className={`${styles.lastSeen} ${styles.off}`}>
      {lastSeenText(player.last_seen_at)}
    </span>
  );
}

/**
 * `formatRelativeLong` for this one column, with its first minute reworded.
 *
 * That formatter says "just now" under a minute, which is the right word in
 * the Created and Joined columns it was written for and the wrong one here:
 * presence expires at exactly 60 seconds, so the row whose last heartbeat was
 * 40 seconds ago is drawn WITHOUT the online dot — and "just now" beside no dot
 * reads as a bug in the dot. "Less than a minute ago" says the same fact
 * without claiming the present tense.
 *
 * Reworded here rather than in `formatRelativeLong`: the guild lists, the
 * Members table and the Blocked table have no 60-second threshold beside them,
 * and this column is the only place where the two disagree.
 *
 * A `last_seen_at` that will not parse falls through to the "—" that formatter
 * returns for it — the panel's own glyph for a value it cannot read, and the
 * one the Updated column two cells along already prints. Not `Never`: this
 * player HAS been seen, and the column's business is not to invent a date the
 * server failed to send.
 */
function lastSeenText(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isNaN(t) && Date.now() - t < 60_000) {
    return "Less than a minute ago";
  }
  return formatRelativeLong(iso);
}

// === Loading skeleton (8 rows, mockup notes) ======================== //

function LoadingTable() {
  return (
    <table className={styles.table} aria-busy="true">
      <thead>
        <tr>
          <th style={{ width: "34%" }}>Player</th>
          <th style={{ width: 96 }}>Country</th>
          <th style={{ width: 150 }}>Last seen</th>
          <th>Joined</th>
          <th>Updated</th>
          <th style={{ width: 36 }} aria-label="Open player" />
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 8 }).map((_, i) => (
          <tr key={i}>
            <td>
              <div className={styles.skelCell}>
                <Skeleton width={32} height={32} radius={999} />
                <div className={styles.skelLines}>
                  <Skeleton width="40%" height={12} />
                  <Skeleton width="28%" height={10} />
                </div>
              </div>
            </td>
            {/* Flag + code together are about 40px wide. */}
            <td>
              <Skeleton width={40} height={12} />
            </td>
            {/* Last seen — "18 minutes ago" is the widest of its three. */}
            <td>
              <Skeleton width={84} height={12} />
            </td>
            <td>
              <Skeleton width={72} height={12} />
            </td>
            <td>
              <Skeleton width={48} height={12} />
            </td>
            <td>
              <Skeleton width={14} height={14} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// === Pagination footer ============================================== //

/**
 * `1–25 of 412`, and no noun.
 *
 * The mockup drops it (players-list-online.html, both populated states) and
 * the reason is on the same screen: with `Online only` on, the chip above the
 * table calls this very number "412 online" while the footer called it "412
 * players" — one number under two words, and the footer's was the wrong one,
 * doubly so with `Show bots` on, where part of that 412 is the simulation. The
 * range and the total are the whole of what a pager has to say; which set they
 * count is what the toolbar above is for.
 */
interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  itemsOnPage: number;
  onGoToPage: (page: number) => void;
}

function Pagination({
  page,
  totalPages,
  total,
  itemsOnPage,
  onGoToPage,
}: PaginationProps) {
  const from = (page - 1) * PLAYERS_PAGE_SIZE + 1;
  const to = (page - 1) * PLAYERS_PAGE_SIZE + itemsOnPage;
  return (
    <div className={styles.pagination}>
      <span>
        {from.toLocaleString()}–{to.toLocaleString()} of{" "}
        <b>{total.toLocaleString()}</b>
      </span>
      <div className={styles.pages}>
        <button
          type="button"
          className={styles.pageBtn}
          disabled={page <= 1}
          onClick={() => onGoToPage(page - 1)}
        >
          <ChevronLeft size={12} aria-hidden="true" /> Prev
        </button>
        {pageRange(page, totalPages).map((p, i) =>
          p === "gap" ? (
            <span key={`gap-${i}`} className={styles.pageEllipsis}>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`${styles.pageBtn} ${
                p === page ? styles.current : ""
              }`}
              aria-current={p === page ? "page" : undefined}
              onClick={() => onGoToPage(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className={styles.pageBtn}
          disabled={page >= totalPages}
          onClick={() => onGoToPage(page + 1)}
        >
          Next <ChevronRight size={12} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
